import React, { useCallback, useMemo } from 'react';
import { FormattedDate, FormattedTime } from 'react-intl';

import { useMessage } from '@tmapy/intl';
import { useSelector } from '@tmapy/redux';

import './DocRow.sass';
import './DocList.scss';

import type { DocListResponse } from '../../openapi/types';
import { RouteId } from '../../routes';
import { useHistoryAction, useAppContext } from '../../hooks';

import { getCmdCtrlUrl } from '../../utils/getcmdctrlurl';
import { prejimkaTypesMessages } from './docsMessages';
import { DocBadge } from './DocBadge';
import { AttachmentIcon } from './AttachmentIcon';
import { ResponsiblePersonCell } from './ResponsiblePersonColumn';
import { DOC_STATE, HISTORY_CLOSED, usableFilterItemsForAppType } from '../constants';
import { getDocumentStates } from './DocList';

export const DocListItemDmvsDti: React.FC<DocListResponse> = (props) => {
  const { appType } = useAppContext();
  const routeToDoc = useHistoryAction('dmvsDtiDetail' as RouteId, { appType, id: props.id });
  const formatMessage = useMessage();
  const customerType = useSelector((state) => state.app.config.customerType);
  const usableItems = usableFilterItemsForAppType[customerType];

  const handleClick = useCallback(
    (event: React.MouseEvent) => {
      if (event.ctrlKey || event.metaKey) {
        window.open(getCmdCtrlUrl('dmvsDtiDetail' as RouteId, { appType, id: props.id }), '_blank');
        return; // Ponech nativní chování
      }

      const selectText = window.getSelection();
      if (!selectText || selectText.toString() === '') {
        routeToDoc();
      }
    },
    [routeToDoc],
  );

  const subjekt = `${props.subjekt?.jmeno ?? ''}`.trim();
  const editor_manager = props.editor_subjekt?.jmeno ?? '';

  const historyClosed = useMemo(() => {
    if (!usableItems.includes('history')) return null;

    const closeStates = getDocumentStates(HISTORY_CLOSED, ['CLOSED'] as DOC_STATE[]);
    const filteredHistoryItem = props.history.find((item) => closeStates.includes(item.state));
    return filteredHistoryItem?.updated || null;
  }, [props.history]);

  return (
    <tr className='dmx-docRow' onClick={handleClick}>
      <td className='sg-a-ta-c'>
        <DocBadge
          docState={props.state}
          flow={props.flow}
          rejectedBySubjekty={props.rejected_by_subjekty}
        />
        {props.id && <div className='sg-u-type-bold'>{props.id}</div>}
      </td>
      <td>
        {props.name && <div className='sg-u-type-bold sg-u-type-base'>{props.name}</div>}
        {props.description && <div className='sg-u-c-body-darkest'>{props.description}</div>}
      </td>
      {usableItems.includes('owner') && <td>{props.owner_name}</td>}
      <td>{formatMessage.lookup(prejimkaTypesMessages, props.prejimka_type)}</td>
      <td className='sg-a-ta-r sg-a-ws-nw sg-a-fvn-tn'>
        {props.created && (
          <div>
            <FormattedDate value={new Date(props.created)} />{' '}
            <FormattedTime value={new Date(props.created)} />
          </div>
        )}
        {historyClosed && (
          <div className='dmx-u-c-cta-red'>
            <FormattedDate value={new Date(historyClosed)} />{' '}
            <FormattedTime value={new Date(historyClosed)} />
          </div>
        )}
      </td>
      <td>{props.dmvs_id_podani || '-'}</td>
      <td>{props.dmvs_source || '-'}</td>
      <td>{subjekt}</td>
      <td>{editor_manager}</td>
      <ResponsiblePersonCell responsible_person={props.responsible_person} />
      <td className='sg-a-w-0'>
        {props.attachments_count > 0 && <AttachmentIcon count={props.attachments_count} />}
      </td>
    </tr>
  );
};
